import { useEffect, useState } from 'react'

import getAllTermsAccruedDealerReserve from 'api/application/getAllTermsAccruedDealerReserve'
import getAllTermsDealerReserve from 'api/application/getAllTermsDealerReserve'
import { AxiosError } from 'axios'
import { OctaneLogger } from 'integrations/datadog/OctaneLogger'

export interface UseMaxDealerReservePerTermInterface {
    reservesByTermId: Record<number, number>
    loading: boolean
    error: Error | null
}

const useMaxDealerReservePerTerm = (
    applicationUuid: string | undefined,
): UseMaxDealerReservePerTermInterface => {
    const [reservesByTermId, setReservesByTermId] = useState<
        Record<number, number>
    >({})

    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<Error | null>(null)

    useEffect(() => {
        const abortController = new AbortController()

        if (!applicationUuid) {
            setReservesByTermId({})
            setLoading(false)
            setError(null)

            return
        }

        // Clear stale reserves from a previous application so they are never
        // stamped onto the new application's terms during the fetch window.
        setReservesByTermId({})
        setLoading(true)

        const fetchReserves = async () => {
            const [dealerItems, accruedItems] = await Promise.all([
                getAllTermsDealerReserve(applicationUuid, abortController),
                getAllTermsAccruedDealerReserve(
                    applicationUuid,
                    abortController,
                ),
            ])

            const standardByTermId = Object.fromEntries(
                dealerItems.map((item) => [
                    item.approval_term_id,
                    item.reserve_value,
                ]),
            )

            const accruedByTermId = Object.fromEntries(
                accruedItems.map((item) => [
                    item.approval_term_id,
                    item.reserve_value,
                ]),
            )

            const termIds = new Set([
                ...Object.keys(standardByTermId),
                ...Object.keys(accruedByTermId),
            ])

            const result = Object.fromEntries(
                [...termIds].map((termId) => [
                    termId,
                    (standardByTermId[termId] ?? 0) +
                        (accruedByTermId[termId] ?? 0),
                ]),
            )

            setReservesByTermId(result)
            setError(null)
            setLoading(false)
        }

        fetchReserves().catch((fetchError: AxiosError) => {
            if (fetchError.code === AxiosError.ERR_CANCELED) {
                return
            }

            OctaneLogger.error(
                'Error when fetching dealer reserves for all terms.',
                { applicationUuid },
                fetchError,
            )

            setError(fetchError)
            setLoading(false)
        })

        return () => abortController.abort()
    }, [applicationUuid])

    return {
        reservesByTermId,
        loading,
        error,
    }
}

export default useMaxDealerReservePerTerm
